/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { InjectionToken } from '@angular/core';
import {
  Evaluator,
  EvaluatorForStudent,
  JobDetails,
  Mentee,
  MenteeAssignment,
  Mentor,
  MentorshipImportReport,
  MentorshipStatus,
  RelatedUserStatus,
  Student,
  StudentAssignment,
  StudentForEvaluator,
} from '@app/app/page-modules/admin/components/mentors/admin-mentors.model';
import { CommentRequest } from '@app/app/page-modules/reviews/models';
import { EditorContent } from '@app/app/shared/models';
import { MembersBooleanSearchRequest } from '@app/app/shared/models/admin/boolean-filters.model';
import { UserSearch } from '@app/app/shared/models/admin/members.model';
import { Page } from '@app/app/shared/models/page';
import { ObservableResult } from '@app/app/shared/store';

export const MENTOR_MENTEE_DATA_SERVICE = new InjectionToken<MentorMenteeDataService>('[Mentor Mentee] DataService');

export interface CreateJobRequest {
  title: string;
  level: string;
  startDate: string;
  endDate?: string;
  evaluatorUserUid: string;
  studentUserUid: string;
}

export interface AcknowledgeFormRequest {
  acknowledged: boolean;
}

export interface PendingMenteesDecisionRequest {
  approvedUsers: Set<string>;
  rejectedUsers: Set<string>;
}

export interface FeedbackItemRefPlaylist {
  type: 'Playlist';
  playlistUid: string;
}

export interface FeedbackEventItemRefForm {
  type: 'FORM';
  formUid: string;
  cardUid: string;
}

export interface MentorFeedbackEventNote {
  uid: string;
  author?: string;
  item: FeedbackEventItemRefForm;
  metadata: Metadata;
  message: EditorContent[];
}

export type MentorFeedbackEvent = MentorFeedbackEventNote;

export interface Metadata {
  createdOn: string;
  lastUpdatedOn?: string;
}

export interface MentorFeedback {
  _id: string;
  menteeUserUid: string;
  mentorUserUid: string;
  mentorName?: string;
  item: FeedbackItemRefPlaylist;
  events: MentorFeedbackEvent[];
  metadata?: Metadata;
  userWorkspacesUid: string;
  organizationUid: string;
  seenFormUids: string[];
  updatedFormUids: string[];
}

export interface MenteePlaylistSummary {
  playlistUid: string;
  uri: string;
  title: string;
  startedOn: string;
  completedOn: string;
  lastUpdatedOn: string;
  totalMentorComments: number;
  latestLearnerFormUpdate: string;
  latestSubmissionAcknowledgement: string | null;
  unseenFormsCount: number;
}

export interface RelatedUserSearchItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  imageUrl?: string;
  status: RelatedUserStatus;
  since: string;
  jobs: JobDetails[];
  folioUri?: string;
  isActiveMenteeOfRequestingUser: boolean;
  isActiveStudentOfRequestingUser: boolean;
}

export interface MentorMenteeDataService {
  searchByMentor(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Mentor>>;
  searchByMentee(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Mentee>>;
  searchByEvaluator(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Evaluator>>;
  searchByStudent(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Student>>;
  findStudentsForEvaluator(
    page: number,
    size: number,
    evaluatorUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<StudentForEvaluator>>;
  findEvaluatorsForStudent(
    page: number,
    size: number,
    studentUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<EvaluatorForStudent>>;
  findMenteesForMentor(
    page: number,
    size: number,
    mentorUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<Mentee>>;
  findMentorsForMentee(
    page: number,
    size: number,
    menteeUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<Mentor>>;
  findMentors(page: number, size: number, term?: string, statuses?: MentorshipStatus[]): ObservableResult<Page<Mentor>>;
  assignMenteeToMentor(mentorUserUid: string, menteeUserUid: string, status: MentorshipStatus): ObservableResult<MenteeAssignment>;
  deleteMenteeFromMentor(mentorUserUid: string, menteeUserUid: string): ObservableResult<void>;
  assignStudentToEvaluator(evaluatorUserUid: string, studentUserUid: string, status: MentorshipStatus): ObservableResult<StudentAssignment>;
  deleteStudentFromEvaluator(evaluatorUserUid: string, studentUserUid: string): ObservableResult<void>;
  importMentees(menteesFile: File, sendNotifications: boolean): ObservableResult<MentorshipImportReport>;
  postPendingMenteesDecision(decision: PendingMenteesDecisionRequest): ObservableResult<void>;
  findPendingMentees(): ObservableResult<UserSearch[]>;
  getAllFeedbackForLearnerPlaylist(playlistUid: string, learnerUserUid: string): ObservableResult<MentorFeedback[]>;
  getSingleMentorFeedbackForLearnerPlaylist(
    playlistUid: string,
    learnerUserUid: string,
    mentorUserUid: string,
  ): ObservableResult<MentorFeedback>;
  createNoteOnForm(
    playlistUid: string,
    cardUid: string,
    formUid: string,
    learnerUserUid: string,
    request: CommentRequest,
  ): ObservableResult<MentorFeedbackEventNote>;
  updateNoteOnForm(
    playlistUid: string,
    cardUid: string,
    formUid: string,
    learnerUserUid: string,
    noteUid: string,
    request: CommentRequest,
  ): ObservableResult<MentorFeedbackEventNote>;
  deleteNoteOnForm(playlistUid: string, cardUid: string, formUid: string, learnerUserUid: string, noteUid: string): ObservableResult<void>;
  getSubmissionsFromMentee(
    mentorUserUid: string,
    menteeUserUid: string,
    page: number,
    size: number,
  ): ObservableResult<Page<MenteePlaylistSummary>>;
  getMentee(menteeUserUid: string): ObservableResult<Mentee>;
  getRelatedUser(relatedUserUid: string): ObservableResult<RelatedUserSearchItem>;
  patchFormAcknowledgement(
    playlistUid: string,
    cardUid: string,
    formUid: string,
    menteeUserUid: string,
    isAcknowledged: boolean,
  ): ObservableResult<void>;
  getAllJobs(): ObservableResult<JobDetails[]>;
  createJob(request: CreateJobRequest): ObservableResult<JobDetails>;
  assignJobToStudentEvaluator(evaluatorUserUid: string, studentUserUid: string, jobId: string): ObservableResult<void>;
  removeJobFromStudentEvaluator(evaluatorUserUid: string, studentUserUid: string, jobId: string): ObservableResult<void>;
}
